import { FC, useContext, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import { Button, DatePicker, Input, Modal } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { WorkerSuggest } from 'components';
import { LocalizationContext } from 'localization';
import { CREATE_TASK, DELETE_TASK, GET_TASKS, UPDATE_TASK } from 'schemas';
import { IWorker, TTask } from 'types';

import s from './TaskManager.module.scss';

interface ITasksBlockProps {
  task: TTask | null;
  onClose: VoidFunction;
}

interface IFormValues {
  title: string;
  assigned_to: Omit<IWorker, 'creator'> | null;
  deadline_at: string | null;
}

const DEFAULT_VALUES: IFormValues = {
  title: '',
  assigned_to: null,
  deadline_at: null,
};

export const TaskManager: FC<ITasksBlockProps> = ({ task, onClose }) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (task) {
      reset({
        ...DEFAULT_VALUES,
        ...task,
        deadline_at: task.deadline_at ?? null,
      });

      trigger();

      return;
    }

    reset(DEFAULT_VALUES);
  }, [task, reset, trigger]);

  const [createTask, { loading: createTaskLoading }] = useMutation(
    CREATE_TASK,
    {
      refetchQueries: [GET_TASKS],
      awaitRefetchQueries: true,
      onCompleted: onClose,
      onError: console.error,
    }
  );
  const [updateTask, { loading: updateTaskLoading }] = useMutation(
    UPDATE_TASK,
    {
      refetchQueries: [GET_TASKS],
      awaitRefetchQueries: true,
      onCompleted: onClose,
      onError: console.error,
    }
  );
  const [deleteTask, { loading: deleteTaskLoading }] = useMutation(
    DELETE_TASK,
    {
      refetchQueries: [GET_TASKS],
      awaitRefetchQueries: true,
      onCompleted: onClose,
      onError: console.error,
    }
  );

  return (
    <Modal onClose={onClose} disableOutsideClose>
      <form
        className={s.container}
        onSubmit={handleSubmit(({ title, assigned_to, deadline_at }) => {
          if (!permissions?.has('worker_task_manage')) return;
          const date = dayjs(deadline_at).format('YYYY-MM-DD 23:59:59');

          if (task) {
            updateTask({
              variables: {
                id: task.id,
                input: {
                  title,
                  ...(assigned_to ? { assigned_to: assigned_to.id } : {}),
                  ...(deadline_at ? { deadline_at: date } : {}),
                },
              },
            }).catch(console.error);
            return;
          }

          createTask({
            variables: {
              input: {
                title,
                ...(assigned_to ? { assigned_to: assigned_to.id } : {}),
                ...(deadline_at ? { deadline_at: date } : {}),
              },
            },
          }).catch(console.error);
        })}
      >
        <header className={s.header}>
          {t(task ? 'task.editing' : 'task.creating')}
        </header>
        <div className={s['fields-container']}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <div className={s.field}>
                {t('task')}
                <Input
                  {...field}
                  variant="secondary"
                  error={error?.message}
                  placeholder={t('task.placeholder')}
                />
              </div>
            )}
            name="title"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 3,
                message: t('errors.minLength', 3),
              },
              maxLength: {
                value: 60,
                message: t('errors.maxLength', 60),
              },
            }}
          />
        </div>
        <div className={s['fields-container']}>
          <Controller
            render={({ field }) => (
              <div className={cn(s.field, s.field_date)}>
                {t('task.period')}
                <DatePicker
                  date={field.value || dayjs().format()}
                  setDate={field.onChange}
                  firstValidDate={dayjs().format()}
                  lastValidDate={dayjs().add(30, 'day').format()}
                  withTime={false}
                  fullWidth
                  lang={lang}
                />
              </div>
            )}
            name="deadline_at"
            control={control}
          />
          <Controller
            //TODO: fix ref issue
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            render={({ field: { ref, ...field } }) => (
              <div className={s.field}>
                {t('task.executor')}
                <WorkerSuggest {...field} />
              </div>
            )}
            name="assigned_to"
            control={control}
          />
        </div>
        <div className={s['submit-container']}>
          {task && (
            <Button
              onClick={() => deleteTask({ variables: { id: task.id } })}
              variant="outline"
              color="red"
              fullWidth
              disabled={
                createTaskLoading ||
                updateTaskLoading ||
                deleteTaskLoading ||
                !permissions?.has('worker_task_manage')
              }
            >
              {t('delete')}
            </Button>
          )}
          <Button
            type="submit"
            fullWidth
            disabled={
              !isDirty ||
              createTaskLoading ||
              updateTaskLoading ||
              deleteTaskLoading ||
              !permissions?.has('worker_task_manage')
            }
          >
            {task ? t('save') : t('create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
